import type { ApiProduct, Cart } from "@/lib/api";

/**
 * Accessories to suggest on the cart page: what the customer might like next.
 *
 * The add-ons the admin set on the desks in the cart come first, because
 * someone chose them to go with that desk. Then the other accessories, as
 * the catalogue orders them. Nothing already in the cart, nothing sold out,
 * nothing that isn't an accessory.
 */
export function suggestAccessories(products: ApiProduct[], cart: Cart | null, limit = 3): ApiProduct[] {
  if (!cart || cart.items.length === 0) return [];
  const inCart = new Set([
    ...cart.items.map((i) => i.productSlug),
    ...(cart.upgrades ?? []).map((u) => u.productSlug),
  ]);
  const bySlug = new Map(products.map((p) => [p.slug, p]));
  const sellable = (p: ApiProduct | undefined): p is ApiProduct =>
    Boolean(p) && p!.line === "accessory" && !p!.soldOut && !inCart.has(p!.slug);

  const paired = cart.items
    .map((i) => bySlug.get(i.productSlug))
    .flatMap((p) => p?.addons ?? [])
    .map((a) => bySlug.get(a.sku?.product?.slug ?? ""));
  const rest = products.filter((p) => p.line === "accessory");

  const out: ApiProduct[] = [];
  for (const p of [...paired, ...rest]) {
    if (sellable(p) && !out.includes(p)) out.push(p);
    if (out.length >= limit) break;
  }
  return out;
}
